import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen, Sparkles, QrCode, Globe, Route, Calculator, HelpCircle, Mail,
  Clock, Users, CalendarDays, FileSpreadsheet, ShieldCheck, Languages,
  ChevronRight, ScanLine, CheckCircle2, MessageSquare, FileDown,
} from "lucide-react";
import { API } from "../App";
import { PublicHeader } from "../components/PublicHeader";
import { PublicFooter } from "../components/PublicFooter";
import { Button } from "../components/ui/button";

const IMG = {
  team: "https://images.unsplash.com/photo-1758691736975-9f7f643d178e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHw0fHxkaXZlcnNlJTIwbXVsdGluYXRpb25hbCUyMHRlYW0lMjBzbWlsaW5nJTIwd29ya3BsYWNlfGVufDB8fHx8MTc5MDIzMTE1OHww&ixlib=rb-4.1.0&q=85&w=1200",
  devices: "https://images.unsplash.com/photo-1426024084828-5da21e13f5dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHx0YWJsZXQlMjBsYXB0b3AlMjBzbWFydHBob25lJTIwYnVzaW5lc3MlMjBhcHAlMjBzY3JlZW58ZW58MHx8fHwxNzkwMjMxMTU4fDA&ixlib=rb-4.1.0&q=85&w=1200",
  qrscan: "https://images.unsplash.com/photo-1559131397-f94da358f7ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHw0fHxzY2FubmluZyUyMFFSJTIwY29kZSUyMHdpdGglMjB0YWJsZXR8ZW58MHx8fHwxNzkwMjMxMTU4fDA&ixlib=rb-4.1.0&q=85&w=1200",
  multicultural: "https://images.unsplash.com/photo-1572021335469-31706a17aaef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxtdWx0aWN1bHR1cmFsJTIwY293b3JrZXJzJTIwdGVhbXdvcmslMjBvZmZpY2V8ZW58MHx8fHwxNzkwMjMxMTU4fDA&ixlib=rb-4.1.0&q=85&w=1200",
  simulator: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxsYXB0b3AlMjBkYXNoYm9hcmQlMjBhbmFseXRpY3MlMjBjYWxjdWxhdG9yJTIwZGVza3xlbnwwfHx8fDE3OTAyMzExNTh8MA&ixlib=rb-4.1.0&q=85&w=1200",
};

const TOC = [
  { id: "overview", icon: BookOpen, label: "PRC勤怠とは" },
  { id: "features", icon: Sparkles, label: "できること" },
  { id: "punch", icon: QrCode, label: "QR打刻の使い方" },
  { id: "multilang", icon: Globe, label: "多言語対応" },
  { id: "flow", icon: Route, label: "導入の流れ" },
  { id: "estimate", icon: Calculator, label: "見積の取り方" },
  { id: "faq", icon: HelpCircle, label: "よくある質問" },
  { id: "contact", icon: Mail, label: "お問い合わせ" },
];

const FEATURES = [
  { icon: QrCode, title: "QRコードで打刻顔監視", desc: "従業員証のQRコードをかざすだけ。出勤・退勤を数秒で記録できます。" },
  { icon: Clock, title: "労働時間の自動計算", desc: "出勤切り捨て・退勤切り上げなど、15分刻みの丸め計算を自動化します。" },
  { icon: Users, title: "従業員・有給管理", desc: "従業員情報を一元管理。有給の申請・承認もオンラインで完結します。" },
  { icon: CalendarDays, title: "シフト管理", desc: "カレンダー上でシフトを作成・割り当て。テンプレートで効率化できます。" },
  { icon: FileSpreadsheet, title: "給与・賃金台帳・年末調整", desc: "勤怠データから給与を集計し、法定帳票の作成・出力にも対応します。" },
  { icon: Languages, title: "6言語対応", desc: "日本語・英語・ポルトガル語・ベトナム語・タガログ語・スペイン語に対応。" },
];

const PUNCH_STEPS = [
  { icon: QrCode, title: "企業コードを入力", desc: "打刻用のタブレットやスマホで、はじめに一度だけ企業コードを入力します。2回目以降は自動で記憶されます。" },
  { icon: ScanLine, title: "QRコードをかざす", desc: "従業員証に印刷されたQRコードをカメラにかざすと、その場で本人を認識します。" },
  { icon: CheckCircle2, title: "打刻完了", desc: "出勤・退勤を自動判定して記録。従業員の母国語で挨拶メッセージと音声が流れます。" },
];

const FLOW_STEPS = [
  { title: "お見積り", desc: "「導入費用シミュレーター」で概算費用をその場で確認し、そのまま見積を依頼できます。" },
  { title: "お申し込み・ご契約", desc: "担当者より正式なお見積書をご案内。内容にご納得いただけたらお申し込みください。" },
  { title: "初期設定", desc: "管理画面から従業員を登録し、QRコードを発行・印刷します。設定代行オプションもご用意しています。" },
  { title: "運用開始", desc: "打刻用の端末を入口などに設置すれば運用開始。スマホ・タブレットでもそのまま使えます。" },
];

const ESTIMATE_STEPS = [
  "プランを選ぶ（従業員数の規模に合わせて選択）",
  "お支払いサイクルを選ぶ（月額 / 年額）",
  "従業員数を入力する",
  "必要なオプション（機器・設定代行など）を選ぶ",
  "その場で表示される概算を確認し、フォームから見積を依頼する",
];

const FAQS = [
  { q: "顔認証は使えますか？", a: "現在はQRコード認証に一本化しています。従業員証のQRコードをかざすだけで、なりすましを防ぎつつスピーディに打刻できます。" },
  { q: "スマホやタブレットで使えますか？", a: "はい。PWA対応のため、専用アプリのインストール不要でスマホ・タブレット・PCのブラウザからご利用いただけます。ホーム画面に追加すればアプリのように使えます。" },
  { q: "外国人スタッフでも使えますか？", a: "はい。打刻画面の表示と音声の挨拶が従業員の母国語（6言語）で行われるため、日本語が不慣れなスタッフでも安心してご利用いただけます。" },
  { q: "導入までどれくらいかかりますか？", a: "従業員登録とQRコードの発行が完了すればすぐに運用を開始できます。初期設定の代行オプションもご利用いただけます。" },
  { q: "料金はどのように決まりますか？", a: "プラン・従業員数・オプションによって決まります。「導入費用シミュレーター」で概算をその場でご確認いただけます。" },
];

export default function GuidePage() {
  const [active, setActive] = useState("overview");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollTo = (id) => {
    setActive(id);
    const el = document.getElementById(`guide-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const downloadPdf = () => {
    const a = document.createElement("a");
    a.href = `${API}/site/guide/pdf`;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col" data-testid="guide-page">
      <PublicHeader />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200 dark:border-slate-800">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-slate-50 to-slate-100 dark:from-emerald-950/30 dark:via-slate-950 dark:to-slate-900" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/70 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 text-sm font-medium mb-6">
              <BookOpen className="w-4 h-4 text-emerald-500" />
              <span>はじめての方へ</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight mb-5">
              PRC勤怠
              <span className="block text-emerald-500">取扱説明書</span>
            </h1>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl">
              はじめてご覧になる方・導入をご検討中の方に向けて、PRC勤怠でできることと、
              打刻から導入・お見積りまでの流れを分かりやすくご案内します。
            </p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mt-8">
              <Link to="/estimate">
                <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl w-full sm:w-auto" data-testid="guide-hero-estimate-btn">
                  <Calculator className="w-5 h-5 mr-2" />
                  導入費用を試算する
                </Button>
              </Link>
              <Button
                size="lg"
                variant="outline"
                className="rounded-xl w-full sm:w-auto"
                onClick={downloadPdf}
                data-testid="guide-download-pdf-btn"
              >
                <FileDown className="w-5 h-5 mr-2" />
                PDFをダウンロード
              </Button>
              <Button
                size="lg"
                variant="ghost"
                className="rounded-xl w-full sm:w-auto"
                onClick={() => scrollTo("features")}
                data-testid="guide-hero-features-btn"
              >
                できることを見る
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-3 bg-emerald-500/10 rounded-[2rem] blur-2xl hidden lg:block" aria-hidden="true" />
            <img
              src={IMG.team}
              alt="多様な人材が活躍する職場"
              loading="eager"
              className="relative w-full h-64 sm:h-80 lg:h-[26rem] object-cover rounded-3xl shadow-2xl ring-1 ring-black/5"
              data-testid="guide-hero-image"
            />
            <div className="absolute -bottom-4 -left-2 sm:left-4 bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-100 dark:border-slate-800 px-4 py-3 flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center flex-shrink-0">
                <QrCode className="w-5 h-5 text-white" />
              </span>
              <div className="text-left">
                <p className="text-sm font-bold leading-tight">かざすだけ打刻</p>
                <p className="text-xs text-slate-500">多様な人材が活躍できる職場へ</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-[240px_1fr] gap-10">
          {/* 目次 */}
          <aside className="lg:sticky lg:top-24 self-start">
            <p className="text-xs font-semibold text-slate-400 px-2 mb-2 hidden lg:block">目次</p>
            <nav className="flex lg:flex-col gap-1.5 overflow-x-auto pb-2 lg:pb-0">
              {TOC.map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    onClick={() => scrollTo(t.id)}
                    data-testid={`guide-toc-${t.id}`}
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left text-sm whitespace-nowrap transition-colors flex-shrink-0 ${
                      active === t.id
                        ? "bg-emerald-600 text-white shadow-sm"
                        : "text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-900 border border-transparent hover:border-slate-200 dark:hover:border-slate-800"
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span>{t.label}</span>
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* 本文 */}
          <div className="space-y-14 min-w-0">
            {/* Overview */}
            <section id="guide-overview" className="scroll-mt-24" data-testid="guide-section-overview">
              <SectionHeading icon={BookOpen}>PRC勤怠とは</SectionHeading>
              <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                PRC勤怠は、多国籍チームでも「シンプルに」使える勤怠管理システムです。
                従業員証のQRコードをかざすだけで出退勤を記録でき、労働時間の集計から給与・法定帳票の作成までを一気通貫でサポートします。
                専用アプリのインストールは不要で、スマホ・タブレット・PCのブラウザからそのままご利用いただけます。
              </p>
              <div className="mt-6 rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-sm">
                <img
                  src={IMG.devices}
                  alt="スマホ・タブレット・PCで使えるPRC勤怠"
                  loading="lazy"
                  className="w-full h-52 sm:h-64 object-cover"
                  data-testid="guide-overview-image"
                />
              </div>
              <div className="grid sm:grid-cols-3 gap-4 mt-6">
                {[
                  { icon: QrCode, t: "かざすだけ打刻", d: "従業員証のQRで数秒" },
                  { icon: Globe, t: "6言語対応", d: "外国人スタッフも安心" },
                  { icon: ShieldCheck, t: "なりすまし防止", d: "本人確認で正確に記録" },
                ].map((c, i) => (
                  <div key={i} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
                    <c.icon className="w-6 h-6 text-emerald-500 mb-3" />
                    <p className="font-semibold text-sm">{c.t}</p>
                    <p className="text-xs text-slate-500 mt-1">{c.d}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Features */}
            <section id="guide-features" className="scroll-mt-24" data-testid="guide-section-features">
              <SectionHeading icon={Sparkles}>できること</SectionHeading>
              <div className="grid sm:grid-cols-2 gap-4">
                {FEATURES.map((f, i) => {
                  const Icon = f.icon;
                  return (
                    <div
                      key={i}
                      className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 hover:shadow-lg hover:border-emerald-300 dark:hover:border-emerald-800 transition-all"
                      data-testid={`guide-feature-${i}`}
                    >
                      <div className="w-11 h-11 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center mb-3">
                        <Icon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                      </div>
                      <p className="font-semibold mb-1">{f.title}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{f.desc}</p>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Punch */}
            <section id="guide-punch" className="scroll-mt-24" data-testid="guide-section-punch">
              <SectionHeading icon={QrCode}>QR打刻の使い方（3ステップ）</SectionHeading>
              <p className="text-slate-600 dark:text-slate-300 mb-6">
                従業員の方は、入口などに設置された端末で以下の手順で打刻します。むずかしい操作は一切ありません。
              </p>
              <div className="mb-6 rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 shadow-sm">
                <img
                  src={IMG.qrscan}
                  alt="端末にQRコードをかざして打刻する様子"
                  loading="lazy"
                  className="w-full h-52 sm:h-64 object-cover"
                  data-testid="guide-punch-image"
                />
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                {PUNCH_STEPS.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div key={i} className="relative rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid={`guide-punch-step-${i}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="w-11 h-11 rounded-xl bg-emerald-600 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-3xl font-bold text-slate-100 dark:text-slate-800 tabular-nums">{i + 1}</span>
                      </div>
                      <p className="font-semibold mb-1">{s.title}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{s.desc}</p>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-slate-400 mt-4">
                ※ 音声が鳴らない場合は、端末の音量・サイレントスイッチをご確認ください。オフライン時も端末に保存され、オンライン復帰時に自動同期します。
              </p>
            </section>

            {/* Multilang */}
            <section id="guide-multilang" className="scroll-mt-24" data-testid="guide-section-multilang">
              <SectionHeading icon={Globe}>多言語対応</SectionHeading>
              <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden grid lg:grid-cols-2">
                <img
                  src={IMG.multicultural}
                  alt="多国籍のスタッフが一緒に働く様子"
                  loading="lazy"
                  className="w-full h-56 lg:h-full object-cover"
                  data-testid="guide-multilang-image"
                />
                <div className="p-6">
                  <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-5">
                    従業員ごとに母国語を設定できます。打刻画面の表示と、打刻完了時の挨拶メッセージ・音声がその言語で再生されるため、
                    日本語が不慣れなスタッフでも迷わず利用できます。
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {["日本語", "English", "Português", "Tiếng Việt", "Tagalog", "Español"].map((l) => (
                      <span key={l} className="px-3 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-sm font-medium">
                        {l}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Flow */}
            <section id="guide-flow" className="scroll-mt-24" data-testid="guide-section-flow">
              <SectionHeading icon={Route}>導入の流れ</SectionHeading>
              <ol className="space-y-3">
                {FLOW_STEPS.map((s, i) => (
                  <li key={i} className="flex gap-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid={`guide-flow-step-${i}`}>
                    <span className="flex-shrink-0 w-9 h-9 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center tabular-nums">
                      {i + 1}
                    </span>
                    <div>
                      <p className="font-semibold mb-0.5">{s.title}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{s.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            {/* Estimate */}
            <section id="guide-estimate" className="scroll-mt-24" data-testid="guide-section-estimate">
              <SectionHeading icon={Calculator}>見積の取り方</SectionHeading>
              <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden grid lg:grid-cols-2">
                <div className="p-6">
                  <p className="text-slate-600 dark:text-slate-300 mb-4">
                    「導入費用シミュレーター」なら、以下のステップで概算費用がその場で分かり、そのまま見積依頼まで行えます。
                  </p>
                  <ul className="space-y-2.5 mb-6">
                    {ESTIMATE_STEPS.map((s, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-slate-600 dark:text-slate-300">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/estimate">
                    <Button className="bg-emerald-600 hover:bg-emerald-700 rounded-xl" data-testid="guide-estimate-cta-btn">
                      <Calculator className="w-4 h-4 mr-2" />
                      シミュレーターを開く
                    </Button>
                  </Link>
                </div>
                <div className="relative order-first lg:order-last">
                  <img
                    src={IMG.simulator}
                    alt="導入費用シミュレーターのイメージ"
                    loading="lazy"
                    className="w-full h-52 lg:h-full object-cover"
                    data-testid="guide-estimate-image"
                  />
                  <div className="absolute top-4 right-4 bg-white dark:bg-slate-900 rounded-xl shadow-lg border border-slate-100 dark:border-slate-800 px-4 py-2">
                    <p className="text-[10px] text-slate-500">概算費用（例）</p>
                    <p className="text-lg font-bold text-emerald-600 leading-tight">月額 12,800円<span className="text-[10px] text-slate-400 font-normal">〜</span></p>
                  </div>
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section id="guide-faq" className="scroll-mt-24" data-testid="guide-section-faq">
              <SectionHeading icon={HelpCircle}>よくある質問</SectionHeading>
              <div className="space-y-3">
                {FAQS.map((f, i) => (
                  <div key={i} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid={`guide-faq-${i}`}>
                    <p className="font-semibold flex items-start gap-2 mb-2">
                      <MessageSquare className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      {f.q}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed pl-7">{f.a}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact CTA */}
            <section id="guide-contact" className="scroll-mt-24" data-testid="guide-section-contact">
              <div className="rounded-3xl bg-slate-900 dark:bg-slate-800 p-8 sm:p-10 text-center">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">まずは概算費用をチェック</h2>
                <p className="text-slate-400 mb-7 max-w-xl mx-auto">
                  プランや従業員数を選ぶだけで、導入にかかる費用がその場で分かります。ご不明点はお気軽にお問い合わせください。
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/estimate">
                    <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 rounded-xl w-full sm:w-auto" data-testid="guide-contact-estimate-btn">
                      <Calculator className="w-5 h-5 mr-2" />
                      導入費用を試算する
                    </Button>
                  </Link>
                  <Button
                    size="lg"
                    variant="outline"
                    className="rounded-xl w-full sm:w-auto border-white text-white hover:bg-white/10"
                    onClick={downloadPdf}
                    data-testid="guide-contact-pdf-btn"
                  >
                    <FileDown className="w-5 h-5 mr-2" />
                    取扱説明書をPDFで保存
                  </Button>
                  <Link to="/p/contact">
                    <Button size="lg" variant="outline" className="rounded-xl w-full sm:w-auto border-white text-white hover:bg-white/10" data-testid="guide-contact-inquiry-btn">
                      <Mail className="w-5 h-5 mr-2" />
                      お問い合わせ
                    </Button>
                  </Link>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}

const SectionHeading = ({ icon: Icon, children }) => (
  <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2.5 mb-4">
    <span className="w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
      <Icon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
    </span>
    {children}
  </h2>
);
